/**
 * DateRangePicker.js
 *
 * Admin-style analytics date-range selector for the Management module. Replaces
 * the old 7/30/90 preset-only `SegmentedControl` filter with a richer picker:
 *
 *   - a compact trigger button (active range label + `calendar-range` icon) that
 *     opens a NON-GLASSY bottom-sheet modal (solid surface, per the workspace
 *     "no glassy modals" mandate), and
 *   - inside the sheet, the quick presets (`MANAGEMENT_RANGE_PRESETS`, 7/30/90)
 *     as selectable chips PLUS a hand-rolled month calendar grid (mirroring
 *     screens/MonthlyCalendarView.js — no `react-native-calendars` dependency)
 *     for choosing an arbitrary start->end range with two taps.
 *
 * The two-tap flow: first tap sets the range start, the second sets the end
 * (taps before the start swap the bounds); the in-between span is highlighted in
 * the orange brand colour. Prev/next month navigation, an Apply action (emits the
 * selection) and a Clear action (resets to the 30-day default) round it out.
 *
 * @module components/management/DateRangePicker
 */

import React, {useCallback, useMemo, useState} from 'react';
import {Modal, Pressable, ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from './SafeIcon';
import {useManagementTheme} from '../../helpers/managementTheme';
import {MANAGEMENT_RANGE_PRESETS, getManagementDateRange} from '../../helpers/managementApi';

/** Milliseconds in a day — used to derive inclusive day counts between two dates. */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Strip the time component so two dates on the same calendar day compare equal.
 * @param {Date} date any date.
 * @returns {Date} a new date at local midnight.
 */
const atMidnight = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

/**
 * True when both dates fall on the same calendar day (year/month/day match).
 * @param {?Date} a first date.
 * @param {?Date} b second date.
 * @returns {boolean}
 */
const isSameDay = (a, b) => !!a && !!b
    && a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();

/**
 * Short, locale-aware day label (e.g. "júl. 1.") used to build the range label
 * and the in-sheet summary line.
 * @param {Date|string|number} value a date-ish value.
 * @returns {string}
 */
const formatShortDay = (value) => {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('hu-HU', {month: 'short', day: 'numeric'});
};

/**
 * Build a human-friendly "start – end" range label (e.g. "júl. 1. – júl. 18.").
 * @param {Date|string|number} start range start.
 * @param {Date|string|number} end range end.
 * @returns {string}
 */
const formatRangeLabel = (start, end) => `${formatShortDay(start)} – ${formatShortDay(end)}`;

/**
 * Inclusive whole-day count between two dates (min 1).
 * @param {Date|string|number} start range start.
 * @param {Date|string|number} end range end.
 * @returns {number}
 */
const dayCountBetween = (start, end) => {
    const a = atMidnight(new Date(start)).getTime();
    const b = atMidnight(new Date(end)).getTime();
    return Math.max(1, Math.round(Math.abs(b - a) / MS_PER_DAY) + 1);
};

/**
 * Generate the Monday-first month grid cells (including the leading/trailing
 * days from the neighbouring months so every week row is full), mirroring
 * screens/MonthlyCalendarView.js.
 * @param {Date} monthDate any date within the month to render.
 * @returns {Array<{date: Date, isCurrentMonth: boolean}>}
 */
const buildMonthCells = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const firstDayOfWeek = firstDay.getDay();
    const leading = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
    const totalDays = lastDay.getDate();
    const totalCells = Math.ceil((totalDays + leading) / 7) * 7;

    const cells = [];
    for (let i = 0; i < leading; i += 1) {
        cells.push({date: new Date(year, month, -leading + i + 1), isCurrentMonth: false});
    }
    for (let i = 1; i <= totalDays; i += 1) {
        cells.push({date: new Date(year, month, i), isCurrentMonth: true});
    }
    const trailing = totalCells - cells.length;
    for (let i = 1; i <= trailing; i += 1) {
        cells.push({date: new Date(year, month + 1, i), isCurrentMonth: false});
    }
    return cells;
};

/**
 * Premium, admin-style analytics date-range picker.
 *
 * @param {object} props
 * @param {?number} [props.days] active preset day-count (7/30/90) when a preset is
 *   selected; `null`/absent when a custom range is active.
 * @param {?(Date|string)} [props.startDate] active custom-range start (ISO/Date).
 * @param {?(Date|string)} [props.endDate] active custom-range end (ISO/Date).
 * @param {?string} [props.label] pre-formatted label for the active custom range;
 *   overrides the derived label on the trigger button when present.
 * @param {boolean} [props.disabled=false] disables the trigger.
 * @param {boolean} [props.compact=false] render the trigger as a content-sized pill instead of a full-width row.
 * @param {(selection: {days: ?number, startDate: ?string, endDate: ?string, label: string}) => void} props.onChange
 *   called when the user applies a selection. For a preset `days` is 7/30/90 with
 *   derived ISO `startDate`/`endDate`; for a custom range `days` is `null` with
 *   explicit ISO `startDate`/`endDate`; `label` is always a display string.
 * @returns {JSX.Element}
 */
const DateRangePicker = ({days = null, startDate = null, endDate = null, label = '', disabled = false, compact = false, onChange}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();

    const [open, setOpen] = useState(false);
    const [viewMonth, setViewMonth] = useState(() => new Date());
    // Pending in-sheet selection (applied only on "Apply").
    const [pendingDays, setPendingDays] = useState(days || null);
    const [tempStart, setTempStart] = useState(startDate ? atMidnight(new Date(startDate)) : null);
    const [tempEnd, setTempEnd] = useState(endDate ? atMidnight(new Date(endDate)) : null);

    const hasCustom = !!startDate && !!endDate;

    /** Label shown on the collapsed trigger button. */
    const triggerLabel = useMemo(() => {
        if (hasCustom) return label || formatRangeLabel(startDate, endDate);
        const preset = MANAGEMENT_RANGE_PRESETS.find((p) => p.value === days);
        return preset?.label || t('mgmt_dash_range_days', {defaultValue: 'Utolsó {{days}} nap', days: days || 30});
    }, [hasCustom, label, startDate, endDate, days, t]);

    const weekdays = useMemo(() => ([
        t('calendar_weekday_mon', {defaultValue: 'H'}),
        t('calendar_weekday_tue', {defaultValue: 'K'}),
        t('calendar_weekday_wed', {defaultValue: 'Sze'}),
        t('calendar_weekday_thu', {defaultValue: 'Cs'}),
        t('calendar_weekday_fri', {defaultValue: 'P'}),
        t('calendar_weekday_sat', {defaultValue: 'Szo'}),
        t('calendar_weekday_sun', {defaultValue: 'V'}),
    ]), [t]);

    const monthLabel = useMemo(
        () => viewMonth.toLocaleDateString('hu-HU', {year: 'numeric', month: 'long'}),
        [viewMonth],
    );

    const weeks = useMemo(() => {
        const cells = buildMonthCells(viewMonth);
        const rows = [];
        for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
        return rows;
    }, [viewMonth]);

    /** Open the sheet, seeding the pending state from the current active selection. */
    const openSheet = useCallback(() => {
        if (disabled) return;
        setPendingDays(hasCustom ? null : (days || 30));
        setTempStart(startDate ? atMidnight(new Date(startDate)) : null);
        setTempEnd(endDate ? atMidnight(new Date(endDate)) : null);
        setViewMonth(startDate ? new Date(startDate) : new Date());
        setOpen(true);
    }, [disabled, hasCustom, days, startDate, endDate]);

    const goPrevMonth = useCallback(() => {
        setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }, []);

    const goNextMonth = useCallback(() => {
        setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }, []);

    /** Select a quick preset — clears any pending custom range. */
    const selectPreset = useCallback((value) => {
        setPendingDays(value);
        setTempStart(null);
        setTempEnd(null);
    }, []);

    /** Handle a calendar-day tap (two-tap start->end range selection). */
    const selectDay = useCallback((date) => {
        const day = atMidnight(date);
        setPendingDays(null);
        // Begin a fresh range on the first tap, or once a full range already exists.
        if (!tempStart || (tempStart && tempEnd)) {
            setTempStart(day);
            setTempEnd(null);
            return;
        }
        // Second tap: a day before the start swaps the bounds so start <= end.
        if (day.getTime() < tempStart.getTime()) {
            setTempEnd(tempStart);
            setTempStart(day);
        } else {
            setTempEnd(day);
        }
    }, [tempStart, tempEnd]);

    /** Whether the current pending selection can be applied. */
    const canApply = (!!tempStart && !!tempEnd) || pendingDays != null;

    /** Emit the pending selection and close the sheet. */
    const apply = useCallback(() => {
        if (tempStart && tempEnd) {
            const start = atMidnight(tempStart);
            const end = new Date(tempEnd);
            end.setHours(23, 59, 59, 999);
            onChange?.({
                days: null,
                startDate: start.toISOString(),
                endDate: end.toISOString(),
                label: formatRangeLabel(start, end),
            });
        } else if (pendingDays != null) {
            const range = getManagementDateRange(pendingDays);
            const preset = MANAGEMENT_RANGE_PRESETS.find((p) => p.value === pendingDays);
            onChange?.({
                days: pendingDays,
                startDate: range.startDate,
                endDate: range.endDate,
                label: preset?.label || t('mgmt_dash_range_days', {defaultValue: 'Utolsó {{days}} nap', days: pendingDays}),
            });
        }
        setOpen(false);
    }, [tempStart, tempEnd, pendingDays, onChange, t]);

    /** Reset the picker back to the 30-day default and apply it. */
    const clear = useCallback(() => {
        const fallback = 30;
        const range = getManagementDateRange(fallback);
        const preset = MANAGEMENT_RANGE_PRESETS.find((p) => p.value === fallback);
        setPendingDays(fallback);
        setTempStart(null);
        setTempEnd(null);
        onChange?.({
            days: fallback,
            startDate: range.startDate,
            endDate: range.endDate,
            label: preset?.label || t('mgmt_dash_range_days', {defaultValue: 'Utolsó {{days}} nap', days: fallback}),
        });
        setOpen(false);
    }, [onChange, t]);

    const summaryText = useMemo(() => {
        if (tempStart && tempEnd) {
            return `${formatRangeLabel(tempStart, tempEnd)}  ·  ${t('mgmt_range_picker_days_count', {defaultValue: '{{count}} nap', count: dayCountBetween(tempStart, tempEnd)})}`;
        }
        if (tempStart) {
            return t('mgmt_range_picker_pick_end', {defaultValue: 'Válaszd ki a záró napot'});
        }
        return t('mgmt_range_picker_hint', {defaultValue: 'Koppints a kezdő, majd a záró napra'});
    }, [tempStart, tempEnd, t]);

    /** Resolve the highlight state of a single calendar cell. */
    const cellState = useCallback((date) => {
        const isStart = isSameDay(date, tempStart);
        const isEnd = isSameDay(date, tempEnd);
        let inRange = false;
        if (tempStart && tempEnd) {
            const d = atMidnight(date).getTime();
            inRange = d > atMidnight(tempStart).getTime() && d < atMidnight(tempEnd).getTime();
        }
        return {isStart, isEnd, inRange, isEdge: isStart || isEnd};
    }, [tempStart, tempEnd]);

    return (
        <>
            <TouchableOpacity
                activeOpacity={0.85}
                disabled={disabled}
                onPress={openSheet}
                style={[styles.trigger, compact && styles.triggerCompact, {backgroundColor: m.colors.surface, borderColor: m.colors.border, opacity: disabled ? 0.6 : 1}]}
            >
                <View style={[styles.triggerIcon, {backgroundColor: m.colors.chipBg}]}>
                    <Icon name="calendar-range" size={18} color={m.colors.primary} />
                </View>
                <Text numberOfLines={1} style={[compact ? styles.triggerLabelCompact : styles.triggerLabel, {color: m.colors.heading}]}>{triggerLabel}</Text>
                <Icon name="chevron-down" size={20} color={m.colors.muted} />
            </TouchableOpacity>

            <Modal visible={open} transparent animationType="slide" onRequestClose={() => setOpen(false)}>
                <View style={styles.backdrop}>
                    <Pressable style={styles.backdropFill} onPress={() => setOpen(false)} />
                    {/* Solid (non-glassy) sheet surface per the no-glassy-modals mandate. */}
                    <View style={[styles.sheet, {backgroundColor: m.colors.surface, borderColor: m.colors.border}]}>
                        <View style={styles.grabber}><View style={[styles.grabberBar, {backgroundColor: m.colors.border}]} /></View>

                        <View style={styles.sheetHeader}>
                            <Text style={[styles.sheetTitle, {color: m.colors.heading}]}>{t('mgmt_range_picker_title', {defaultValue: 'Időszak kiválasztása'})}</Text>
                            <TouchableOpacity onPress={() => setOpen(false)} hitSlop={8}>
                                <Icon name="close" size={22} color={m.colors.muted} />
                            </TouchableOpacity>
                        </View>

                        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.sheetBody}>
                            <Text style={[styles.groupLabel, {color: m.colors.subText}]}>{t('mgmt_range_picker_presets_title', {defaultValue: 'Gyors időszakok'})}</Text>
                            <View style={styles.chipRow}>
                                {MANAGEMENT_RANGE_PRESETS.map((preset) => {
                                    const active = pendingDays === preset.value;
                                    return (
                                        <TouchableOpacity
                                            key={preset.value}
                                            activeOpacity={0.85}
                                            onPress={() => selectPreset(preset.value)}
                                            style={[
                                                styles.chip,
                                                {backgroundColor: active ? m.colors.primary : m.colors.surfaceAlt, borderColor: active ? m.colors.primary : m.colors.border},
                                            ]}
                                        >
                                            <Text style={[styles.chipText, {color: active ? '#FFFFFF' : m.colors.subText}]}>{preset.label}</Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>

                            <Text style={[styles.groupLabel, {color: m.colors.subText, marginTop: 18}]}>{t('mgmt_range_picker_custom_title', {defaultValue: 'Egyéni időszak'})}</Text>

                            <View style={styles.monthNav}>
                                <TouchableOpacity onPress={goPrevMonth} hitSlop={8} style={[styles.navBtn, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                                    <Icon name="chevron-left" size={22} color={m.colors.text} />
                                </TouchableOpacity>
                                <Text style={[styles.monthLabel, {color: m.colors.heading}]}>{monthLabel}</Text>
                                <TouchableOpacity onPress={goNextMonth} hitSlop={8} style={[styles.navBtn, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                                    <Icon name="chevron-right" size={22} color={m.colors.text} />
                                </TouchableOpacity>
                            </View>

                            <View style={styles.weekdayRow}>
                                {weekdays.map((wd, idx) => (
                                    <View key={idx} style={styles.weekdayCell}>
                                        <Text style={[styles.weekdayText, {color: m.colors.muted}]}>{wd}</Text>
                                    </View>
                                ))}
                            </View>

                            <View style={styles.grid}>
                                {weeks.map((week, wIdx) => (
                                    <View key={wIdx} style={styles.weekRow}>
                                        {week.map((cell, cIdx) => {
                                            const {isStart, isEnd, inRange, isEdge} = cellState(cell.date);
                                            return (
                                                <TouchableOpacity
                                                    key={cIdx}
                                                    activeOpacity={0.8}
                                                    onPress={() => selectDay(cell.date)}
                                                    style={styles.dayCellWrap}
                                                >
                                                    <View style={[
                                                        styles.dayInRangeBg,
                                                        inRange && {backgroundColor: m.colors.chipBg},
                                                        isStart && tempEnd && styles.dayEdgeStartBg,
                                                        isStart && tempEnd && {backgroundColor: m.colors.chipBg},
                                                        isEnd && styles.dayEdgeEndBg,
                                                        isEnd && {backgroundColor: m.colors.chipBg},
                                                    ]} />
                                                    <View style={[
                                                        styles.dayCell,
                                                        isEdge && {backgroundColor: m.colors.primary},
                                                    ]}>
                                                        <Text style={[
                                                            styles.dayText,
                                                            {color: cell.isCurrentMonth ? m.colors.text : m.colors.muted},
                                                            inRange && {color: m.colors.primary, fontWeight: '800'},
                                                            isEdge && {color: '#FFFFFF', fontWeight: '900'},
                                                        ]}>
                                                            {cell.date.getDate()}
                                                        </Text>
                                                    </View>
                                                </TouchableOpacity>
                                            );
                                        })}
                                    </View>
                                ))}
                            </View>

                            <View style={[styles.summary, {backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}>
                                <Icon name="calendar-range" size={16} color={m.colors.primary} />
                                <Text style={[styles.summaryText, {color: m.colors.text}]}>{summaryText}</Text>
                            </View>
                        </ScrollView>

                        <View style={[styles.footer, {borderTopColor: m.colors.border}]}>
                            <TouchableOpacity
                                activeOpacity={0.85}
                                onPress={clear}
                                style={[styles.footerBtn, styles.footerBtnGhost, {borderColor: m.colors.border}]}
                            >
                                <Icon name="refresh" size={18} color={m.colors.subText} />
                                <Text style={[styles.footerBtnText, {color: m.colors.subText}]}>{t('mgmt_range_picker_clear', {defaultValue: 'Alaphelyzet'})}</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                activeOpacity={0.85}
                                onPress={apply}
                                disabled={!canApply}
                                style={[styles.footerBtn, {backgroundColor: m.colors.primary, opacity: canApply ? 1 : 0.5}]}
                            >
                                <Icon name="check" size={18} color="#FFFFFF" />
                                <Text style={[styles.footerBtnText, {color: '#FFFFFF'}]}>{t('mgmt_range_picker_apply', {defaultValue: 'Alkalmaz'})}</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </View>
            </Modal>
        </>
    );
};

const styles = StyleSheet.create({
    trigger: {flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 8},
    triggerCompact: {alignSelf: 'flex-start', paddingRight: 14},
    triggerIcon: {width: 30, height: 30, borderRadius: 15, alignItems: 'center', justifyContent: 'center'},
    triggerLabel: {flex: 1, fontSize: 14, fontWeight: '700'},
    triggerLabelCompact: {fontSize: 14, fontWeight: '700', flexShrink: 1},

    backdrop: {flex: 1, backgroundColor: 'rgba(2,6,23,0.56)', justifyContent: 'flex-end'},
    backdropFill: {...StyleSheet.absoluteFillObject},
    sheet: {borderTopLeftRadius: 30, borderTopRightRadius: 30, borderWidth: 1, maxHeight: '90%', paddingBottom: 8},
    grabber: {alignItems: 'center', paddingTop: 10, paddingBottom: 4},
    grabberBar: {width: 42, height: 5, borderRadius: 999},

    sheetHeader: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 20, paddingTop: 6, paddingBottom: 10},
    sheetTitle: {fontSize: 18, fontWeight: '900'},
    sheetBody: {paddingHorizontal: 20, paddingBottom: 16},

    groupLabel: {fontSize: 11.5, fontWeight: '900', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 10},
    chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
    chip: {borderRadius: 999, borderWidth: 1, paddingHorizontal: 16, paddingVertical: 9},
    chipText: {fontSize: 13, fontWeight: '800'},

    monthNav: {flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12},
    navBtn: {width: 38, height: 38, borderRadius: 19, borderWidth: 1, alignItems: 'center', justifyContent: 'center'},
    monthLabel: {fontSize: 15.5, fontWeight: '900', textTransform: 'capitalize'},

    weekdayRow: {flexDirection: 'row', marginBottom: 4},
    weekdayCell: {flex: 1, alignItems: 'center', paddingVertical: 6},
    weekdayText: {fontSize: 11.5, fontWeight: '800'},

    grid: {},
    weekRow: {flexDirection: 'row'},
    dayCellWrap: {flex: 1, height: 42, alignItems: 'center', justifyContent: 'center'},
    dayInRangeBg: {...StyleSheet.absoluteFillObject, marginVertical: 4},
    dayEdgeStartBg: {left: '50%'},
    dayEdgeEndBg: {right: '50%'},
    dayCell: {width: 34, height: 34, borderRadius: 999, alignItems: 'center', justifyContent: 'center'},
    dayText: {fontSize: 13.5, fontWeight: '700'},

    summary: {flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 12, marginTop: 16},
    summaryText: {flex: 1, fontSize: 13, fontWeight: '700'},

    footer: {flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingTop: 12, borderTopWidth: 1},
    footerBtn: {flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, borderRadius: 999, paddingVertical: 15},
    footerBtnGhost: {borderWidth: 1},
    footerBtnText: {fontSize: 14, fontWeight: '900'},
});

export default DateRangePicker;
